import {
  Button,
  ButtonIconPosition,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import {
  Checkbox,
  CheckboxColorScheme,
} from "@amzn/stencil-react-components/checkbox";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import IconExternalLinkSmall from "@amzn/stencil-react-icons/icons/icon-external-link-small";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { ActionType, AppContext } from "../../stores";
import {
  createSideBySidePopup,
  resizeWizardForSideBySide,
} from "../Commons/popup-helper";
import { STEP_ID } from "../constants";

/**
 * A step to help NHs to create their Amazon passwords.
 */
export default function AmazonPassword(): JSX.Element {
  const { state, dispatch } = useContext(AppContext);
  const [passwordPopupWindow, setPasswordPopupWindow] = useState<Window | null>(
    null
  );
  const intl = useIntl();
  const regularFontWeight = "regular";

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    updateOnboardingStep("D1_PASSWORD");

    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-create-amazon-password",
    });
  }, []);

  /**
   * Close open popup when moving on to next step
   */
  /* istanbul ignore next: will cover in integration tests */
  useEffect(() => {
    if (passwordPopupWindow) {
      passwordPopupWindow.close();
      setPasswordPopupWindow(null);
    }
  }, [state.currentStepNum]);

  const handleConfirm = (checked: boolean) => {
    trackStructEvent({
      category: Day1EventCategory.CreateAmazonPassword,
      action: checked
        ? Day1EventAction.CheckedConfirmation
        : Day1EventAction.UncheckedConfirmation,
    });

    /**
     * Enable the next button when NH has confirmed that he/she has created his/her Amazon Password.
     */
    dispatch({
      stepId: STEP_ID.AMAZON_PASSWORD,
      type: ActionType.SET_NEXT_BTN_STATUS,
      enableNext: checked,
    });
  };

  /**
   * Open a window pop up to the Amazon Password portal
   * TODO: Add integration tests to cover this. https://sim.amazon.com/issues/NHE-243
   */
  /* istanbul ignore next */
  const openAmazonPassword = () => {
    if (!passwordPopupWindow) {
      // Track metrics on new window open only. Don't track if the window was already open on click.
      trackStructEvent({
        category: Day1EventCategory.CreateAmazonPassword,
        action: Day1EventAction.OpenPasswordTool,
      });

      const ezoWidthPercentage = 0.4;

      // Open a popup 60% of the screen wide, right justified.
      const passwordWindowRef: Window | null = createSideBySidePopup({
        url: "https://password-v2.corp.amazon.com/newHire",
        ezoWidthPercentage: ezoWidthPercentage,
        windowPopupWidthPercentage: 1 - ezoWidthPercentage,
      });

      // Resize wizard to 60% width and move to top left corner.
      resizeWizardForSideBySide({ ezoWidthPercentage: ezoWidthPercentage });
      passwordWindowRef?.focus();

      // Keep track of if the window has been closed yet or not so we can resize the main window after its been closed.
      // Note that adding an event listener for `close` and reassigning passwordWindowRef.onclose don't work.
      const i = setInterval(() => {
        if (passwordWindowRef?.closed) {
          // clear timer.
          clearInterval(i);

          // resize wizard to full screen.
          window.resizeTo(window.screen.availWidth, window.screen.availHeight);

          // Dereference if the window is closed so that they can open a new window later.
          setPasswordPopupWindow(null);

          trackStructEvent({
            category: Day1EventCategory.CreateAmazonPassword,
            action: Day1EventAction.ClosePasswordToolManual,
          });
        }
      }, 1_000);
      setPasswordPopupWindow(passwordWindowRef);
    } else {
      // Window is still open, focus on it.
      passwordPopupWindow?.focus();
    }
  };

  const stepPadding = { bottom: "S300" };

  return (
    <Col gridGap="S400">
      <StepBodyStyledText>
        <Text fontWeight={regularFontWeight} dataTestId="passwordIntro1">
          <FormattedMessage id="amazonPassword-expander-body-intro1" />
        </Text>
      </StepBodyStyledText>
      <View
        backgroundColor="color.surface.bg-default"
        padding="S200"
        borderRadius="dimensions.border.radius.200"
      >
        <StepBodyStyledText>
          <ol>
            <Col padding={stepPadding}>
              <li>
                <Text fontWeight={regularFontWeight} dataTestId="passwordStep1">
                  <FormattedMessage id="amazonPassword-expander-body-step-1" />
                </Text>
              </li>
            </Col>
            <Col padding={stepPadding}>
              <li>
                <Text fontWeight={regularFontWeight} dataTestId="passwordStep2">
                  <FormattedMessage id="amazonPassword-expander-body-step-2" />
                </Text>
              </li>
            </Col>
            <Col padding={stepPadding}>
              <li>
                <Col>
                  <Text
                    fontWeight={regularFontWeight}
                    dataTestId="passwordStep3.1"
                  >
                    <FormattedMessage id="amazonPassword-expander-body-step-3-1" />
                  </Text>
                  <Text
                    fontWeight={regularFontWeight}
                    dataTestId="passwordStep3.2"
                  >
                    <FormattedMessage id="amazonPassword-expander-body-step-3-2" />
                  </Text>
                  <Text
                    fontWeight={regularFontWeight}
                    dataTestId="passwordStep3.3"
                  >
                    <FormattedMessage id="amazonPassword-expander-body-step-3-3" />
                  </Text>
                </Col>
              </li>
            </Col>
            <Col padding={stepPadding}>
              <li>
                <Text fontWeight={regularFontWeight} dataTestId="passwordStep4">
                  <FormattedMessage id="amazonPassword-expander-body-step-4" />
                </Text>
              </li>
            </Col>
            <Col padding={stepPadding}>
              <li>
                <Text fontWeight={regularFontWeight} dataTestId="passwordStep5">
                  <FormattedMessage id="amazonPassword-expander-body-step-5" />
                </Text>
              </li>
            </Col>
            <Col>
              <li>
                <Text fontWeight={regularFontWeight} dataTestId="passwordStep6">
                  <FormattedMessage id="amazonPassword-expander-body-step-6" />
                </Text>
              </li>
            </Col>
          </ol>
        </StepBodyStyledText>
      </View>
      <MessageBanner
        dataTestId="amazonPasswordBanner"
        type={MessageBannerType.Informational}
      >
        <FormattedMessage id="amazonPassword-expander-body-banner" />
      </MessageBanner>
      <Row>
        <Button
          variant={ButtonVariant.Primary}
          icon={
            <IconExternalLinkSmall
              title={intl.formatMessage({
                id: "amazonPassword-button-createPassword",
              })}
            />
          }
          iconPosition={ButtonIconPosition.Trailing}
          dataTestId="openAmazonPassBtn"
          onClick={openAmazonPassword}
        >
          <Text fontWeight="bold">
            <FormattedMessage id="amazonPassword-button-createPassword" />
          </Text>
        </Button>
      </Row>
      <Row alignItems="center" gridGap="S200">
        <Checkbox
          colorScheme={CheckboxColorScheme.Default}
          id="confirmPasswordCreationCheckBox"
          dataTestId="confirmPasswordCreationCheckBox"
          onChange={(evt) => handleConfirm(evt.target.checked)}
        />
        <Label htmlFor="confirmPasswordCreationCheckBox">
          <Text
            fontWeight={regularFontWeight}
            dataTestId="passwordCreationConfirmationText"
          >
            <FormattedMessage id="amazonPassword-expander-body-confirmation" />
          </Text>
        </Label>
      </Row>
    </Col>
  );
}
